/**
 * OUTFLOW: Sales Invoice Report (AR).
 *
 * Winnie's v5 note asks for the sale side of the ledger the way she filters it in
 * practice: a date range, a sale status, and a customer. The rows are the invoices
 * finalised through the OUTFLOW pipeline, so an invoice only appears here once it
 * exists as a record, not when a deal is merely expected to sell.
 *
 * The money columns are read, not recalculated. On a margin scheme sale the GST sits on
 * the margin rather than the sale price, and re-deriving ex-GST as total divided by 1.1
 * would quietly overstate it. Whatever the invoice was raised for is what shows.
 */

import { supabase } from '../lib/supabase'
import { DateRange, ALL_TIME, inRange, rangeLabel } from '../lib/dateRange'

export type SaleStatusFilter = 'all' | 'sold' | 'active' | 'cancelled'
export type SaleStatus = 'sold' | 'active' | 'cancelled'

export const SALE_STATUS_OPTIONS: { value: SaleStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sold', label: 'Sold' },
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
]

export interface SalesInvoiceRow {
  invoiceId: string
  invoiceNumber: string
  invoiceDate: string | null
  dueDate: string | null
  customer: string
  customerCompany: string
  dealId: string | null
  stockNumber: string
  vin: string
  vehicle: string
  saleStatus: SaleStatus
  saleDate: string | null
  exGst: number
  gst: number
  total: number
  received: number
  outstanding: number
  invoiceStatus: string
  paymentReceivedAt: string | null
}

export interface SalesInvoiceTotals {
  count: number
  exGst: number
  gst: number
  total: number
  received: number
  outstanding: number
}

export interface SalesInvoiceReport {
  rows: SalesInvoiceRow[]
  periodLabel: string
  totals: SalesInvoiceTotals
}

const money = (v: unknown) => Math.round((Number(v) || 0) * 100) / 100
const day = (v: unknown) => (v ? String(v).slice(0, 10) : null)

/**
 * A deal is sold once it has been sold or closed, cancelled when it was cancelled, and
 * active for everything in between. Anything else on the list is still ours.
 */
function saleStatusOf(dealStatus: string): SaleStatus {
  const s = String(dealStatus || '').toLowerCase()
  if (s === 'sold' || s === 'closed') return 'sold'
  if (s === 'cancelled') return 'cancelled'
  return 'active'
}

function vehicleOf(deal: Record<string, unknown> | null): string {
  if (!deal) return 'Vehicle'
  const parts = [deal.autograb_year, deal.autograb_make, deal.autograb_model]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean)
  return parts.length ? parts.join(' ') : 'Vehicle'
}

export async function loadSalesInvoiceReport(
  range: DateRange = ALL_TIME,
  saleStatus: SaleStatusFilter = 'all',
  customerQuery = '',
): Promise<SalesInvoiceReport> {
  const { data, error } = await supabase
    .from('invoices')
    .select(
      'id, deal_id, invoice_number, invoice_date, due_date, buyer_name, buyer_company, ' +
        'purchase_price, gst_amount, total_amount, status, payment_received_at, ' +
        'payment_amount_received, ' +
        'deal:deals!deal_id(id, deal_status, sale_date, sold_at, stock_number, autograb_vin, ' +
        'autograb_year, autograb_make, autograb_model)',
    )
    .order('invoice_date', { ascending: true })

  if (error) throw error

  const needle = customerQuery.trim().toLowerCase()

  const rows: SalesInvoiceRow[] = ((data || []) as unknown as Record<string, unknown>[])
    .map((row) => {
      const deal = (row.deal || null) as Record<string, unknown> | null
      const total = money(row.total_amount)
      const gst = money(row.gst_amount)
      const received = money(row.payment_amount_received)
      return {
        invoiceId: String(row.id),
        invoiceNumber: String(row.invoice_number || ''),
        invoiceDate: day(row.invoice_date),
        dueDate: day(row.due_date),
        customer: String(row.buyer_name || ''),
        customerCompany: String(row.buyer_company || ''),
        dealId: deal ? String(deal.id) : row.deal_id ? String(row.deal_id) : null,
        stockNumber: deal && deal.stock_number != null ? String(deal.stock_number) : '',
        vin: deal && deal.autograb_vin ? String(deal.autograb_vin) : '',
        vehicle: vehicleOf(deal),
        saleStatus: saleStatusOf(deal ? String(deal.deal_status || '') : ''),
        saleDate: deal ? day(deal.sale_date) || day(deal.sold_at) : null,
        exGst: money(total - gst),
        gst,
        total,
        received,
        outstanding: money(total - received),
        invoiceStatus: String(row.status || ''),
        paymentReceivedAt: row.payment_received_at ? String(row.payment_received_at) : null,
      }
    })
    .filter((r) => inRange(r.invoiceDate, range))
    .filter((r) => (saleStatus === 'all' ? true : r.saleStatus === saleStatus))
    .filter((r) => {
      if (!needle) return true
      return (
        r.customer.toLowerCase().includes(needle) ||
        r.customerCompany.toLowerCase().includes(needle) ||
        r.invoiceNumber.toLowerCase().includes(needle) ||
        r.stockNumber.toLowerCase().includes(needle) ||
        r.vin.toLowerCase().includes(needle)
      )
    })

  const totals = rows.reduce(
    (acc, r) => ({
      count: acc.count + 1,
      exGst: money(acc.exGst + r.exGst),
      gst: money(acc.gst + r.gst),
      total: money(acc.total + r.total),
      received: money(acc.received + r.received),
      outstanding: money(acc.outstanding + r.outstanding),
    }),
    { count: 0, exGst: 0, gst: 0, total: 0, received: 0, outstanding: 0 } as SalesInvoiceTotals,
  )

  return { rows, periodLabel: rangeLabel(range), totals }
}

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export function salesInvoiceCsv(rows: SalesInvoiceRow[]): string {
  const head = [
    'Invoice',
    'Invoice date',
    'Due date',
    'Customer',
    'Company',
    'Stock number',
    'VIN',
    'Vehicle',
    'Sale status',
    'Sale date',
    'Ex GST',
    'GST',
    'Total',
    'Received',
    'Outstanding',
    'Invoice status',
  ]
  const body = rows.map((r) =>
    [
      r.invoiceNumber,
      r.invoiceDate || '',
      r.dueDate || '',
      r.customer,
      r.customerCompany,
      r.stockNumber,
      r.vin,
      r.vehicle,
      r.saleStatus === 'sold' ? 'Sold' : r.saleStatus === 'cancelled' ? 'Cancelled' : 'Active',
      r.saleDate || '',
      r.exGst.toFixed(2),
      r.gst.toFixed(2),
      r.total.toFixed(2),
      r.received.toFixed(2),
      r.outstanding.toFixed(2),
      r.invoiceStatus,
    ]
      .map(csvCell)
      .join(','),
  )
  return [head.join(','), ...body].join('\r\n') + '\r\n'
}
